import React, { useState, PropsWithChildren } from 'react';
import { useMutation } from '@apollo/client/react/hooks';
import DeleteIcon from '@mui/icons-material/Delete';
import { gql } from '@yi/core';
import { i18n_remove } from '@yi/i18n/phrases/i18n_remove';

import { notifyBugsnag } from '~/common';

import { StyledMenuItem, StyledSmallSpinner } from './elements';
import { useLocale } from '../LocaleProvider';

interface RemoveFromPlaylistProps {
  playlistId: string;
  contentId: string;
  callback?: () => void;
}

export const RemoveFromPlaylist: React.FC<PropsWithChildren<RemoveFromPlaylistProps>> = ({
  playlistId,
  contentId,
  callback,
}) => {
  const { locale } = useLocale();

  const [isSubmitting, setSubmitting] = useState(false);
  const [removeFromPlaylist, removeFromPlaylistResult] = useMutation<
    gql.typesClient.Mutate_RemoveFromPlaylist,
    gql.typesClient.Mutate_RemoveFromPlaylistVariables
  >(gql.Mutate_RemoveFromPlaylist, {
    onError: error => {
      if (callback) callback();
      notifyBugsnag(error);
      console.warn('error: ', error);
    },
  });

  return (
    <StyledMenuItem
      style={{ gap: '7px' }}
      onClick={async e => {
        e.preventDefault();
        if (!isSubmitting) {
          setSubmitting(true);
          await removeFromPlaylist({
            variables: { playlist_id: playlistId, content_id: contentId },
            update: cache => {
              const data = cache.readQuery<gql.typesClient.Query_Playlist>({
                query: gql.Query_Playlist,
                variables: { id: playlistId },
              });
              if (data?.Playlist) {
                const items = data.Playlist.items;
                if (!items) return;

                const newItems = items.filter(item => item.content.id !== contentId);

                const playlist: gql.typesClient.Query_Playlist_Playlist = data.Playlist;

                cache.writeQuery<gql.typesClient.Query_Playlist>({
                  query: gql.Query_Playlist,
                  data: {
                    Playlist: {
                      ...playlist,
                      items: [...newItems],
                    },
                  },
                });
              }

              setSubmitting(false);
              if (callback) callback();
            },
          });
        }
      }}
    >
      <>
        <DeleteIcon opacity="0.7" />
        {i18n_remove(locale)}
        {removeFromPlaylistResult.loading && <StyledSmallSpinner size="18px" />}
      </>
    </StyledMenuItem>
  );
};
